// FinalRestingPlace.js — the peaceful closing of the memorial page,
// in the night-gold house theme: an earthly place, and a star in the sky.
//
// Honors every way of resting: burial (a place to visit, with directions),
// ashes scattered (a place remembered, no directions), ashes with family
// (no address shown — privacy by design), or the star itself as the
// resting place. Memorials without a restingType behave exactly as
// before: an address means burial-style display.
import React from 'react';
import { Link } from 'react-router-dom';

const FinalRestingPlace = ({ memorial }) => {
  const hasAddress = !!memorial.address;
  const hasStar = !!(memorial.galaxyLocation && memorial.galaxyLocation.starId);
  const type = memorial.restingType || (hasAddress ? 'buried' : null);

  // What the earthly card should say, per resting type
  const earthCard =
    type === 'buried' && hasAddress
      ? {
          icon: '📍',
          label: 'On Earth',
          body: memorial.address,
          sub: null,
          directions: true,
        }
      : type === 'scattered' && hasAddress
      ? {
          icon: '🕊️',
          label: 'Scattered with Love',
          body: memorial.address,
          sub: 'Returned to the earth and sky they loved.',
          directions: false,
        }
      : type === 'family'
      ? {
          icon: '❦',
          label: 'With Family',
          body: 'Their ashes rest with the family — close to the hearts that loved them most.',
          sub: null,
          directions: false,
        }
      : null;

  // Nothing at all to show
  if (!earthCard && !hasStar) return null;

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-10">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Where They Rest
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Final Resting Place
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="mt-4 text-gray-500 text-sm font-light">
          A peaceful place to remember{earthCard?.directions ? ' and visit' : ''}
        </p>
      </div>

      <div className="max-w-2xl mx-auto text-center">
        {/* Earthly resting place */}
        {earthCard && (
          <div className="bg-[#faf9f7] rounded-2xl border border-[#eee9df] p-8 md:p-10 mb-8">
            <div className="mb-6">
              <div
                className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-xl mb-5"
                style={{
                  background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)',
                  boxShadow: '0 0 0 1px rgba(255,215,140,0.5), 0 0 20px rgba(255,215,140,0.25)',
                  color: '#ffe2a8',
                }}
              >
                {earthCard.icon}
              </div>
              <h3
                className="text-[10px] uppercase mb-3"
                style={{ letterSpacing: '0.3em', color: '#b8964f' }}
              >
                {earthCard.label}
              </h3>
              <p className="text-gray-700 leading-relaxed font-light text-base md:text-lg">
                {earthCard.body}
              </p>
              {earthCard.sub && (
                <p className="mt-3 text-gray-400 text-sm font-light italic">
                  {earthCard.sub}
                </p>
              )}
            </div>

            {/* Directions — only when there is truly somewhere to go */}
            {earthCard.directions && (
              <div className="text-center">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(memorial.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sm-btn-primary"
                >
                  Get Directions
                </a>
              </div>
            )}
          </div>
        )}

        {/* Star in the Galaxy of Memorials */}
        {hasStar ? (
          <div
            className="rounded-2xl p-8 md:p-10 mb-8 text-white relative overflow-hidden"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, #1a1530 0%, #05040d 80%)',
              border: '1px solid rgba(203,171,106,0.4)',
            }}
          >
            {/* Faint stars */}
            <div className="absolute top-[16%] left-[14%] w-1 h-1 rounded-full bg-[#c9d4ef] opacity-50 pointer-events-none"></div>
            <div className="absolute top-[28%] right-[16%] w-0.5 h-0.5 rounded-full bg-[#ffe2a8] opacity-60 pointer-events-none"></div>
            <div className="absolute bottom-[22%] left-[22%] w-0.5 h-0.5 rounded-full bg-[#ffe2a8] opacity-40 pointer-events-none"></div>
            <div className="absolute bottom-[30%] right-[26%] w-1 h-1 rounded-full bg-[#c9d4ef] opacity-40 pointer-events-none"></div>

            <div className="relative mb-6">
              {/* Their star, alive */}
              <div
                className="w-2.5 h-2.5 mx-auto rounded-full mb-6"
                style={{
                  background: '#fff3d0',
                  animation: 'frpStarPulse 3.8s ease-in-out infinite',
                }}
              ></div>
              <h3
                className="text-[10px] uppercase mb-3"
                style={{ letterSpacing: '0.3em', color: '#b8964f' }}
              >
                In the Heavens
              </h3>
              <p className="text-xl md:text-2xl font-extralight mb-2" style={{ color: '#ffe9b8', letterSpacing: '0.04em' }}>
                ✦ {memorial.galaxyLocation.starName || 'A Star in the Galaxy'}
              </p>
              <p className="text-white/55 leading-relaxed font-light text-sm">
                {(() => {
                  const region = memorial.galaxyLocation.region || 'Galaxy of Memorials';
                  const inRegion = /^the\s/i.test(region) ? region.replace(/^the\s/i, 'the ') : `the ${region}`;
                  const visit = (type === 'star' || type === 'scattered' || type === 'family') ? ' Wherever you are, you can visit.' : '';
                  return `A star in ${inRegion} shines in their memory — theirs alone, forever.${visit}`;
                })()}
              </p>
            </div>

            <div className="relative text-center">
              <Link
                to={`/galaxy?star=${memorial.galaxyLocation.starId}`}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
                style={{
                  letterSpacing: '0.24em',
                  color: '#ffe2a8',
                  border: '1px solid rgba(184,150,79,0.5)',
                  background: 'rgba(255,255,255,0.05)',
                  textDecoration: 'none',
                }}
              >
                <span aria-hidden="true">🌌</span>
                <span>Visit Their Star</span>
              </Link>
            </div>

            <style>{`
              @keyframes frpStarPulse {
                0%, 100% { box-shadow: 0 0 18px 7px rgba(255,220,150,0.7), 0 0 54px 22px rgba(255,205,120,0.22); }
                50% { box-shadow: 0 0 26px 10px rgba(255,225,160,0.85), 0 0 72px 30px rgba(255,205,120,0.32); }
              }
            `}</style>
          </div>
        ) : (
          // No star yet — for families without an earthly place to visit,
          // the galaxy is the answer. A quiet doorway, not a pitch.
          (type === 'scattered' || type === 'family' || type === 'star') && (
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] uppercase transition-shadow hover:shadow-[0_0_24px_rgba(255,215,140,0.35)]"
                style={{
                  letterSpacing: '0.22em',
                  color: '#ffe2a8',
                  border: '1px solid rgba(184,150,79,0.5)',
                  background: 'linear-gradient(180deg, #171426, #0c0a18)',
                  textDecoration: 'none',
                }}
              >
                ✦ Give them a star in the galaxy — a place to visit, forever
              </Link>
            </div>
          )
        )}

        {/* Additional Information */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 text-gray-400">
            <div style={{ width: '32px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a)' }}></div>
            <span className="text-[11px] font-light uppercase" style={{ letterSpacing: '0.26em' }}>
              A place of peace and remembrance
            </span>
            <div style={{ width: '32px', height: '1px', background: 'linear-gradient(90deg, #cbab6a, transparent)' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalRestingPlace;
